"use client";

// The mode bar (design spec 2026-10-19 §2, §3; NAV-REDESIGN 2026-10-29): the work modes as a
// segmented control centred in the top bar (an icon-only item and a divider stay available). DTP's filter-chip
// look at shell size: a --sunken track with a hairline, 32 px items, 13 / 500, the current mode a
// --card chip with a hairline and --shadow-sm, its icon in --primary-text. They navigate, so they
// are links in a `nav` (`aria-current="page"`), not ARIA tabs. NAV-REDESIGN (2026-10-29): four
// work modes; below 900 px of top bar the labels fold into the tooltip; below 768 px the bar
// takes its own row under the top bar, labelled, scrolling sideways with no scrollbar.
import { LockIcon, type LucideIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";
import { Kbd } from "./kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

function ModeTabs({
  children,
  className,
  "aria-label": label = "Modes",
}: {
  children: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <nav
      aria-label={label}
      data-slot="mode-tabs"
      className={cn("min-w-0 max-md:strip-scroll", className)}
    >
      {/* Phones: 44 px targets (the row is their own there). */}
      <ul className="flex h-9 w-max items-center gap-0.5 rounded-lg border border-border bg-sunken p-0.5 max-md:h-12">
        {children}
      </ul>
    </nav>
  );
}

const ITEM = `relative flex h-8 min-w-8 max-md:h-11 max-md:min-w-11 items-center justify-center gap-1.5 rounded-md border border-transparent px-2.5 text-sm font-medium tracking-[-0.1px] whitespace-nowrap text-secondary transition-colors duration-(--dur-fast) ease-out hover:bg-active/60 hover:text-foreground aria-[current=page]:border-border aria-[current=page]:bg-card aria-[current=page]:text-foreground aria-[current=page]:shadow-sm [&_svg]:size-4 [&_svg]:shrink-0 aria-[current=page]:[&>svg]:text-primary-text ${FOCUS_RING}`;

type ModeTabProps = {
  icon: LucideIcon;
  label: string;
  /** The mode is the current one (never for a locked tab). */
  active?: boolean | undefined;
  /** The switching key shown in the tooltip (`1`–`7`). */
  shortcut?: string | undefined;
  /** `locked`: add-on off (owners and admins only); `read-only`: grace period. */
  state?: "locked" | "read-only" | undefined;
  /** Tooltip text; defaults to the label. */
  hint?: string | undefined;
  /** Icon only at every width. */
  iconOnly?: boolean | undefined;
  /** A router link element (`<Link to=… />`); defaults to `<a>`. */
  render?: React.ReactElement;
  href?: string;
  className?: string;
  "aria-label"?: string;
  [data: `data-${string}`]: string | undefined;
};

function ModeTab({
  icon: Icon,
  label,
  active,
  shortcut,
  state,
  hint,
  iconOnly,
  render,
  className,
  ...props
}: ModeTabProps) {
  // biome-ignore lint/a11y/useValidAnchor: href and children arrive through the trigger's props.
  // biome-ignore lint/a11y/useAnchorContent: see above.
  const element = render ?? <a />;
  const current = active && state !== "locked";
  return (
    <li className="flex">
      <Tooltip>
        <TooltipTrigger
          render={element}
          data-slot="mode-tab"
          data-state={state}
          aria-current={current ? "page" : undefined}
          className={cn(
            ITEM,
            // Four labelled tabs fit down to 900 px of top bar; a phone gives them their own row.
            iconOnly ? "px-0" : "md:@max-[900px]:px-0",
            // A locked tab opens Add-ons: it is never the current page, whatever the router says.
            state === "locked" &&
              "text-muted-foreground aria-[current=page]:border-transparent aria-[current=page]:bg-transparent aria-[current=page]:text-muted-foreground aria-[current=page]:shadow-none",
            className,
          )}
          {...props}
        >
          <Icon aria-hidden="true" />
          <span className={iconOnly ? "sr-only" : "md:@max-[900px]:sr-only"}>{label}</span>
          {state === "locked" ? (
            <LockIcon aria-hidden="true" className="size-3! text-muted-foreground" />
          ) : null}
          {state === "read-only" ? (
            <span
              aria-hidden="true"
              className="absolute top-1 right-1 size-1.5 rounded-full bg-warning"
            />
          ) : null}
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {hint ?? label}
          {shortcut ? <Kbd>{shortcut}</Kbd> : null}
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

/** A hairline between groups of tabs. */
function ModeTabsDivider() {
  return (
    <li aria-hidden="true" data-slot="mode-tabs-divider" className="mx-0.5 h-4 w-px bg-border" />
  );
}

export { ModeTab, ModeTabs, ModeTabsDivider };
